import React from "react";
import { Language } from "@mui/icons-material";
import { Checkbox, FormControlLabel, Grid, Stack } from "@mui/material";
import CustomTextField from "../../../../../components/common/Textfield";
import { RegistryPage } from "../../registry/sitePageRegistry";
import { SEOForm } from "../../types";
import KeywordChipInput from "./KeywordChipInput";
import SectionCard from "./SectionCard";
import TemplateInput from "./TemplateInput";

interface BasicMetaTagsCardProps {
  form: SEOForm;
  onChange: <K extends keyof SEOForm>(field: K, value: SEOForm[K]) => void;
  pageDefinition?: RegistryPage;
  pageNameLocked?: boolean;
}

const BasicMetaTagsCard: React.FC<BasicMetaTagsCardProps> = ({
  form,
  onChange,
  pageDefinition,
  pageNameLocked,
}) => {
  const variables = pageDefinition?.isDynamic
    ? (pageDefinition.variables ?? [])
    : [];

  return (
    <SectionCard icon={<Language color="primary" />} title="Basic Meta Tags">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <CustomTextField
            label="Page Name"
            value={form.pageName}
            onChange={(event) => onChange("pageName", event.target.value)}
            disabled={pageNameLocked}
            helperText={
              pageNameLocked
                ? `Defined by the developer registry — path ${pageDefinition?.path}`
                : "Unique within this website, for example home or about"
            }
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TemplateInput
            label="Meta Title"
            value={form.metaTitle}
            onChange={(next) => onChange("metaTitle", next)}
            variables={variables}
            helperText={`Recommended: 30-60 characters — ${form.metaTitle.length}/60`}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TemplateInput
            multiline
            label="Meta Description"
            value={form.metaDescription}
            onChange={(next) => onChange("metaDescription", next)}
            variables={variables}
            helperText={`Recommended: 120-160 characters — ${form.metaDescription.length}/160`}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomTextField
            label="Focus Keyword"
            value={form.focusKeyword}
            onChange={(event) => onChange("focusKeyword", event.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <KeywordChipInput
            label="Related Keywords"
            value={form.relatedKeywords}
            onChange={(value) => onChange("relatedKeywords", value)}
            placeholder="Add related keywords..."
            helperText="Press Enter, type a comma, or click outside to add a keyword"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.noindex}
                  onChange={(event) =>
                    onChange("noindex", event.target.checked)
                  }
                />
              }
              label="noindex"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.nofollow}
                  onChange={(event) =>
                    onChange("nofollow", event.target.checked)
                  }
                />
              }
              label="nofollow"
            />
          </Stack>
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export default BasicMetaTagsCard;
